import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { t } from "../../i18n";
import "./SettingsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://b.supchats.com";

export default function SettingsPage({ token }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("account");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [newPasswordStrength, setNewPasswordStrength] = useState({ score: 0, message: "", requirements: {} });

  // Данные профиля (об аккаунте)
  const [profile, setProfile] = useState({ email: "", contact_details: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  // Состояние для API-доступа
  const [apiKey, setApiKey] = useState("");
  const [apiLoading, setApiLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Проверка токена и загрузка профиля при загрузке компонента
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const res = await axios.get(`${API_URL}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile({
          email: res.data?.email || "",
          contact_details: res.data?.contact_details || "",
        });
      } catch (error) {
        console.error("Error loading profile", error);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [token, navigate]);

  // Автоматически скрываем уведомления через несколько секунд
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (!apiMessage) return;
    const timer = setTimeout(() => setApiMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [apiMessage]);

  // Локальная проверка сложности пароля (как при регистрации)
  const checkPasswordStrengthLocal = (pwd) => {
    const requirements = {
      minLength: { test: (pwd || '').length >= 8, message: t('Minimum 8 characters') },
      hasUpperCase: { test: /[A-Z]/.test(pwd || ''), message: t('Uppercase letter (A-Z)') },
      hasLowerCase: { test: /[a-z]/.test(pwd || ''), message: t('Lowercase letter (a-z)') },
      hasNumbers: { test: /\d/.test(pwd || ''), message: t('Number (0-9)') }
    };
    const score = Object.values(requirements).filter(r => r.test).length;
    const isValid = score === 4;
    let msg = '';
    if (!isValid) {
      msg = t('Password does not meet security requirements');
    }
    return { isValid, message: msg, requirements, score };
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage(null);
    // Проверка сложности нового пароля (как при регистрации)
    const { isValid, message: strengthMsg } = checkPasswordStrengthLocal(newPassword);
    if (!isValid) {
      setMessage({ type: "error", text: strengthMsg || t('Password does not meet security requirements') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: t('Passwords do not match') });
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/change_password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setMessage({ type: "success", text: t('Password changed successfully') });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setMessage({
          type: "error",
          text: response.data.detail || t('Error changing password'),
        });
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('Error sending request');
      // Если сервер вернул неверный текущий пароль — покажем явное сообщение
      const wrongCurrent = /current password/i.test(detail) || /wrong password/i.test(detail) || /invalid password/i.test(detail);
      setMessage({ type: "error", text: wrongCurrent ? t('Current password is incorrect') : detail });
    }
  };

  const handleSaveProfile = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMessage(null);
    setProfileSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/me`,
        { contact_details: profile.contact_details || "" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProfile((prev) => ({
        ...prev,
        email: res.data?.email || prev.email,
        contact_details: res.data?.contact_details || prev.contact_details,
      }));
      setMessage({ type: "success", text: t('Account information updated') });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.detail || t('Error updating account information'),
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleRequestApi = async () => {
    setApiMessage(null);
    // Проверяем, указаны ли контактные данные
    if (!profile.contact_details || !profile.contact_details.trim()) {
      setApiMessage({
        type: "error",
        text: t('To request API access, please specify your contact information in the "Account" section.'),
      });
      return;
    }

    setApiLoading(true);
    try {
      // Сначала регистрируем/проверяем заявку на доступ
      const res = await axios.post(
        `${API_URL}/api/access-request`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          // Куки не нужны, работаем только по JWT, чтобы избежать CORS с credentials
          withCredentials: false,
        }
      );

      if (res.data?.status === 'approved' && res.data.api_key) {
        setApiKey(res.data.api_key);
        setApiMessage({
          type: "success",
          text: t('API access is approved and API key is available.'),
        });
      } else if (res.data?.status === 'pending') {
        setApiMessage({
          type: "info",
          text: t('Your request for API access has been sent. The administrator will review it and, if approved, an API key will be generated.'),
        });
      } else {
        setApiMessage({
          type: "error",
          text: t('Unexpected response while requesting API access'),
        });
      }
    } catch (error) {
      setApiMessage({
        type: "error",
        text: error.response?.data?.detail || t('Failed to request API access'),
      });
    } finally {
      setApiLoading(false);
    }
  };

  const copyApiKey = () => {
    if (!apiKey) return;
    try {
      navigator.clipboard.writeText(apiKey);
      setApiMessage({
        type: "success",
        text: t('API key copied to clipboard'),
      });
    } catch (e) {
      setApiMessage({
        type: "error",
        text: t('Failed to copy API key'),
      });
    }
  };

  return (
    <div className="settings">
      <div className="settings-sidebar">
        <button
          onClick={() => {
            setActiveSection("account");
            setMessage(null);
            setApiMessage(null);
          }}
          className={activeSection === "account" ? "active" : ""}
        >
          {t('Account')}
        </button>
        <button
          onClick={() => {
            setActiveSection("api");
            setMessage(null);
            setApiMessage(null);
          }}
          className={activeSection === "api" ? "active" : ""}
        >
          API
        </button>
      </div>
      <div className="settings-content" style={{ maxWidth: 800, margin: '0 auto' }}>
        {message && (
          <div
            className={`message ${message.type}`}
            style={{
              position: 'fixed',
              top: 16,
              right: 16,
              zIndex: 50,
              minWidth: 260,
              maxWidth: 420,
              boxShadow: '0 10px 25px rgba(15,23,42,0.15)',
            }}
          >
            {message.text}
          </div>
        )}
        {apiMessage && (
          <div
            className={`message ${apiMessage.type}`}
            style={{
              position: 'fixed',
              top: message ? 72 : 16,
              right: 16,
              zIndex: 50,
              minWidth: 260,
              maxWidth: 420,
              boxShadow: '0 10px 25px rgba(15,23,42,0.15)',
            }}
          >
            {apiMessage.text}
          </div>
        )}

        {/* Account Section */}
        <div
          className={`settings-section ${
            activeSection === "account" ? "active" : ""
          }`}
        >
          <h3>{t('Account')}</h3>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>{t('Email')}</label>
            <input
              type="text"
              value={profile.email}
              disabled
              className="form-input"
            />
          </div>

          <form onSubmit={handleSaveProfile} className="form-group" style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>{t('Contact Information')}</label>
            <textarea
              value={profile.contact_details}
              onChange={(e) =>
                setProfile((prev) => ({ ...prev, contact_details: e.target.value }))
              }
              className="form-input"
              rows={4}
              placeholder={t('Add your contact information so we can reach you about your account and API usage.')}
            />
            <button
              type="submit"
              className="submit-button"
              disabled={profileSaving || profileLoading}
              style={{
                marginTop: 8,
                padding: '8px 18px',
                fontSize: 14,
                maxWidth: 260,
                width: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              {profileSaving ? t('Saving...') : t('Save account info')}
            </button>
          </form>

          <hr style={{ margin: '16px 0' }} />

          <h4 style={{ marginTop: 0 }}>{t('Change Password')}</h4>
          <form onSubmit={handlePasswordChange} className="form-group">
            {/* Скрытое поле username для рекомендаций браузера по паролям */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              style={{
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                height: 0,
                width: 0,
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
            <input
              type="password"
              placeholder={t('Current Password')}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="form-input"
              required
              autoComplete="current-password"
            />
            <input
              type="password"
              placeholder={t('New Password')}
              value={newPassword}
              onChange={(e) => {
                const val = e.target.value;
                setNewPassword(val);
                const res = checkPasswordStrengthLocal(val);
                setNewPasswordStrength(res);
              }}
              className="form-input"
              required
              autoComplete="new-password"
            />
            {newPassword && newPasswordStrength && newPasswordStrength.requirements && (
              <div className="password-requirements" style={{ marginBottom: 8 }}>
                <div className="requirements-title">{t('Password Requirements:')}</div>
                {Object.entries(newPasswordStrength.requirements).map(([key, req]) => (
                  <div key={key} className={`requirement-item ${req.test ? 'met' : 'unmet'}`}>
                    <span className="requirement-icon">{req.test ? '✓' : '✗'}</span>
                    <span className="requirement-text">{req.message}</span>
                  </div>
                ))}
              </div>
            )}
            <input
              type="password"
              placeholder={t('Confirm New Password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="form-input"
              required
              autoComplete="new-password"
            />
            <button
              type="submit"
              className="submit-button"
              style={{
                marginTop: 8,
                padding: '8px 18px',
                fontSize: 14,
                maxWidth: 260,
                width: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              {t('Save Changes')}
            </button>
          </form>
        </div>

        {/* API Section */}
        <div
          className={`settings-section ${
            activeSection === "api" ? "active" : ""
          }`}
        >
          <h3>{t('API')}</h3>

          {!apiKey && (
            <>
              <p style={{ marginBottom: 12 }}>
                {t('To get access to the external API, first fill in your contact information in the "Account" section. This helps us contact you if needed.')}
              </p>
              <button
                type="button"
                className="submit-button"
                onClick={handleRequestApi}
                disabled={apiLoading || profileLoading}
                style={{
                  marginBottom: 12,
                  padding: '8px 18px',
                  fontSize: 14,
                  maxWidth: 260,
                  width: 'auto',
                  alignSelf: 'flex-start',
                }}
              >
                {apiLoading ? t('Requesting API access...') : t('Request API access')}
              </button>
            </>
          )}

          {apiKey && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ display: 'block', marginBottom: 4 }}>{t('Your API Key')}</label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <input
                  type={showApiKey ? "text" : "password"}
                  readOnly
                  value={apiKey}
                  className="form-input"
                  style={{ maxWidth: 360 }}
                />
                <button
                  type="button"
                  className="submit-button"
                  onClick={() => setShowApiKey((prev) => !prev)}
                  style={{
                    padding: '6px 10px',
                    fontSize: 13,
                    maxWidth: 90,
                    width: 'auto',
                  }}
                >
                  {showApiKey ? '🙈' : '👁'}
                </button>
                <button
                  type="button"
                  className="submit-button"
                  onClick={copyApiKey}
                  style={{
                    padding: '6px 14px',
                    fontSize: 13,
                    maxWidth: 120,
                    width: 'auto',
                  }}
                >
                  {t('Copy')}
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                {t('Use the header')} <code>X-API-Key: YOUR_API_KEY</code> {t('to authenticate in the public API.')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

